import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { Button, Col, Flex, Form, Input, Row, Skeleton, Switch, Typography } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { ActivationWarning } from 'src/app/shared/components/common/tag/ActivationWarning'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { getClient } from 'src/app/shared/services/ClientSetupService'
import { colorTextSubtle } from 'src/app/themes/primaryThemeDefault'
import DelayedClientActivation from '../add-client-setup/DelayedClientActivation'
import { convertTime, fetchGlobalExceptionDefaults } from '../edit-client-setup/utils'
import ACHException from './ACHException'
import CheckExceptions from './CheckExceptions'
import ClientInfo from './ClientInfo'
import './index.scss'

const ClientSetupViewRecord = () => {
  const params = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { id } = params
  const [isFetched, setIsFetched] = useState(false)
  const [activationDate, setActivationDate] = useState<Dayjs | null>(null)
  const [disableActivationDate, setDisableActivationDate] = useState<boolean>(false)

  const getClientInfo = async () => {
    if (id) {
      const res = await getClient(id)

      const date = convertTime({
        date: res?.data?.createdAt,
      })

      const formattedData = { ...res?.data, dateCreated: date }

      // Exception cutoff/reminder come from the global System-Wide Rules config, not the Client. The form
      // is empty until this load resolves, so the fields render directly with the global values.
      const globalExceptionDefaults = await fetchGlobalExceptionDefaults()
      if (globalExceptionDefaults) Object.assign(formattedData, globalExceptionDefaults)

      if (formattedData?.activationDate) {
        formattedData.activationDate = dayjs(formattedData.activationDate)
        setActivationDate(formattedData.activationDate)
        const today = dayjs()
        const isBeforeDay = today.isBefore(formattedData.activationDate, 'day')
        if (isBeforeDay) {
          setDisableActivationDate(false)
        } else {
          setDisableActivationDate(true)
        }
      }
      setIsFetched(true)
      return formattedData
    }
    navigate(menuUrls.clientSetup)
  }

  const { control } = useForm<any>({ mode: 'all', defaultValues: async () => await getClientInfo() })

  const handleClickBack = () => {
    // Redirects user to the Client Setup - Landing Screen
    navigate(menuUrls.clientSetup)
  }

  const defaultCheckExceptionCreationType = useWatch({
    control,
    name: 'defaultCheckExceptionCreationType',
  })

  const defaultAchExceptionCreationType = useWatch({
    control,
    name: 'defaultAchExceptionCreationType',
  })

  const isCreateExceptions = defaultCheckExceptionCreationType !== 'NOCREATE'
  const isCreateAchExceptions = defaultAchExceptionCreationType !== 'NOCREATE'

  return (
    <Form name='client-setup-form' className='form-container read-only' layout='vertical'>
      {isFetched ? (
        <>
          {activationDate && !disableActivationDate && <ActivationWarning activationDate={activationDate} />}
          <ClientInfo control={control} />
          <CheckExceptions control={control} isCreateExceptions={isCreateExceptions} />
          <ACHException control={control} isCreateAchExceptions={isCreateAchExceptions} />
          <DelayedClientActivation control={control} disabled={true} />
          <Row gutter={24} align='middle'>
            <Col span={12}>
              <Form.Item>
                <Controller
                  name='archived'
                  control={control}
                  render={({ field }) => (
                    <Flex gap={12}>
                      <Switch {...field} checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
                      <Typography style={{ color: colorTextSubtle }}>
                        {t('ClientSetup:addNewClient.label.archiveClient')}
                      </Typography>
                    </Flex>
                  )}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label='Date Created'>
                <Controller
                  name='dateCreated'
                  control={control}
                  render={({ field }) => <Input {...field} className='ant-input-view-only' />}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>
          <Flex className='button-group' justify='flex-end' gap={24}>
            <Button className='cancel-button' onClick={handleClickBack}>
              Back
            </Button>
          </Flex>
        </>
      ) : (
        <Skeleton />
      )}
    </Form>
  )
}

export default ClientSetupViewRecord
